// src/pages/Settings/Settings.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../../context/AuthContext';
import { useAlpaca } from '../../context/AlpacaContext';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/authUtils';
import ConnectedDevices from './ConnectedDevices';
import styles from './Settings.module.css';

type SettingsTab = 'account' | 'broker' | 'notifications' | 'security' | 'danger';

const TAB_LABELS: { key: SettingsTab; label: string }[] = [
  { key: 'account', label: 'Account' },
  { key: 'broker', label: 'Broker' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'security', label: 'Security' },
  { key: 'danger', label: 'Danger Zone' },
];

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, setupTOTP, verifyTOTPSetup, enableMFA, disableMFA, getMfaStatus, signOut } = useAuth();
  const { isConnected, paperConnected, liveConnected, disconnect, refreshStatus, connectPaper, connectLive } =
    useAlpaca();

  const [activeTab, setActiveTab] = useState<SettingsTab>('account');

  // ── MFA state ──────────────────────────────────────────────
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaLoading, setMfaLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [qrUri, setQrUri] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Change password state ──────────────────────────────────
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // ── Notification preferences state ────────────────────────
  const [notifPhone, setNotifPhone] = useState('');
  const [notifPhoneVerified, setNotifPhoneVerified] = useState(false);
  const [notifSmsEnabled, setNotifSmsEnabled] = useState(false);
  const [notifEmailEnabled, setNotifEmailEnabled] = useState(false);
  const [notifEmail, setNotifEmail] = useState('');
  const [notifInAppEnabled, setNotifInAppEnabled] = useState(true);
  const [notifAlertTypes, setNotifAlertTypes] = useState({
    positionThreshold: true,
    botSignal: true,
    stopTriggered: true,
    unusualVolume: false,
    strategist: false,
    morningBrief: false,
  });
  const [notifThresholds, setNotifThresholds] = useState({
    tp1Pct: '1.0',
    tp2Pct: '2.0',
    tp3Pct: '5.0',
    stopLossPct: '-2.0',
    trailingStopPct: '1.5',
  });
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState('');
  const [notifSuccess, setNotifSuccess] = useState('');
  const [notifVerifyCode, setNotifVerifyCode] = useState('');
  const [notifVerifySending, setNotifVerifySending] = useState(false);
  const [notifVerifyConfirming, setNotifVerifyConfirming] = useState(false);
  const [showVerifyInput, setShowVerifyInput] = useState(false);

  // ── Delete account state ───────────────────────────────────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── Alpaca state ───────────────────────────────────────────
  const [disconnecting, setDisconnecting] = useState(false);
  const [alpacaMessage, setAlpacaMessage] = useState('');

  // Load MFA status on mount
  useEffect(() => {
    (async () => {
      const status = await getMfaStatus();
      setMfaEnabled(status.enabled);
      setMfaLoading(false);
    })();
  }, [getMfaStatus]);

  // Load Alpaca status on mount
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Load notification preferences on mount
  useEffect(() => {
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) {
          setNotifLoading(false);
          return;
        }
        const res = await fetch(`${API_URL}/api/settings/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setNotifPhone(data.phone || '');
          setNotifPhoneVerified(data.phoneVerified || false);
          setNotifSmsEnabled(data.smsEnabled || false);
          setNotifEmailEnabled(data.emailEnabled || false);
          setNotifEmail(data.email || '');
          setNotifInAppEnabled(data.inAppEnabled !== false);
          if (data.alertTypes) setNotifAlertTypes(data.alertTypes);
          if (data.thresholds) {
            setNotifThresholds({
              tp1Pct: String(data.thresholds.tp1Pct ?? '1.0'),
              tp2Pct: String(data.thresholds.tp2Pct ?? '2.0'),
              tp3Pct: String(data.thresholds.tp3Pct ?? '5.0'),
              stopLossPct: String(data.thresholds.stopLossPct ?? '-2.0'),
              trailingStopPct: String(data.thresholds.trailingStopPct ?? '1.5'),
            });
          }
        }
      } catch {
        // Silently fail — use defaults
      } finally {
        setNotifLoading(false);
      }
    })();
  }, []);

  // ── Notification handlers ────────────────────────────────

  const handleSaveNotifications = async () => {
    setNotifError('');
    setNotifSuccess('');
    setNotifSaving(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API_URL}/api/settings/notifications`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: notifPhone,
          smsEnabled: notifSmsEnabled,
          emailEnabled: notifEmailEnabled,
          email: notifEmail,
          inAppEnabled: notifInAppEnabled,
          alertTypes: notifAlertTypes,
          thresholds: {
            tp1Pct: parseFloat(notifThresholds.tp1Pct),
            tp2Pct: parseFloat(notifThresholds.tp2Pct),
            tp3Pct: parseFloat(notifThresholds.tp3Pct),
            stopLossPct: parseFloat(notifThresholds.stopLossPct),
            trailingStopPct: parseFloat(notifThresholds.trailingStopPct),
          },
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save');
      setNotifPhoneVerified(data.phoneVerified || false);
      setNotifSuccess('Notification preferences saved.');
    } catch (err: any) {
      setNotifError(err.message || 'Failed to save notification settings');
    } finally {
      setNotifSaving(false);
    }
  };

  const handleSendVerification = async () => {
    setNotifError('');
    setNotifVerifySending(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');

      // Auto-save settings first so the phone number is in DynamoDB
      const saveRes = await fetch(`${API_URL}/api/settings/notifications`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: notifPhone,
          smsEnabled: notifSmsEnabled,
          emailEnabled: notifEmailEnabled,
          email: notifEmail,
          inAppEnabled: notifInAppEnabled,
          alertTypes: notifAlertTypes,
          thresholds: {
            tp1Pct: parseFloat(notifThresholds.tp1Pct),
            tp2Pct: parseFloat(notifThresholds.tp2Pct),
            tp3Pct: parseFloat(notifThresholds.tp3Pct),
            stopLossPct: parseFloat(notifThresholds.stopLossPct),
            trailingStopPct: parseFloat(notifThresholds.trailingStopPct),
          },
        }),
      });
      const saveData = await saveRes.json();
      if (!saveRes.ok) throw new Error(saveData.error || 'Failed to save settings');

      // Now send the verification code
      const res = await fetch(`${API_URL}/api/settings/notifications/verify-send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send code');
      setShowVerifyInput(true);
      setNotifSuccess('Verification code sent! Check your phone.');
    } catch (err: any) {
      setNotifError(err.message || 'Failed to send verification');
    } finally {
      setNotifVerifySending(false);
    }
  };

  const handleConfirmVerification = async () => {
    setNotifError('');
    setNotifVerifyConfirming(true);
    try {
      const token = await getAuthToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${API_URL}/api/settings/notifications/verify-confirm`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: notifVerifyCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      setNotifPhoneVerified(true);
      setShowVerifyInput(false);
      setNotifVerifyCode('');
      setNotifSuccess('Phone number verified!');
    } catch (err: any) {
      setNotifError(err.message || 'Verification failed');
    } finally {
      setNotifVerifyConfirming(false);
    }
  };

  // ── MFA handlers ───────────────────────────────────────────

  const handleStartSetup = async () => {
    setMfaError('');
    setMfaSuccess('');
    try {
      const { qrUri, secretKey } = await setupTOTP();
      setQrUri(qrUri);
      setSecretKey(secretKey);
      setSetupMode(true);
    } catch (err: any) {
      setMfaError(err.message || 'Failed to start MFA setup');
    }
  };

  const handleVerifyAndEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    setMfaError('');
    setSubmitting(true);
    try {
      await verifyTOTPSetup(verifyCode);
      await enableMFA();
      setMfaEnabled(true);
      setSetupMode(false);
      setQrUri('');
      setSecretKey('');
      setVerifyCode('');
      setMfaSuccess('Two-factor authentication has been enabled.');
    } catch (err: any) {
      setMfaError(err.message || 'Invalid code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisable = async () => {
    setMfaError('');
    setMfaSuccess('');
    setSubmitting(true);
    try {
      await disableMFA();
      setMfaEnabled(false);
      setMfaSuccess('Two-factor authentication has been disabled.');
    } catch (err: any) {
      setMfaError(err.message || 'Failed to disable MFA');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Change password handler ────────────────────────────────

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters.');
      return;
    }

    setChangingPassword(true);
    try {
      // Use Amplify's updatePassword
      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setPasswordSuccess('Password changed successfully.');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      const msg = err.message || 'Failed to change password';
      if (msg.includes('Incorrect')) {
        setPasswordError('Current password is incorrect.');
      } else {
        setPasswordError(msg);
      }
    } finally {
      setChangingPassword(false);
    }
  };

  // ── Alpaca disconnect handler ──────────────────────────────

  const handleDisconnect = async (env?: 'paper' | 'live') => {
    setDisconnecting(true);
    setAlpacaMessage('');
    try {
      await disconnect(env);
      await refreshStatus();
      setAlpacaMessage(env ? `${env} account disconnected.` : 'All accounts disconnected.');
    } catch (err: any) {
      setAlpacaMessage(err.message || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  // Reconnect wipes the stored credentials and redirects straight back through
  // the OAuth flow — the single-button fix for 401-on-stale-credentials.
  const handleReconnect = async (env: 'paper' | 'live') => {
    setDisconnecting(true);
    setAlpacaMessage('');
    try {
      await disconnect(env);
      // Small delay so the CREDS row write settles before OAuth redirect
      await new Promise((r) => setTimeout(r, 300));
      if (env === 'paper') {
        connectPaper();
      } else {
        connectLive();
      }
    } catch (err: any) {
      setAlpacaMessage(err.message || 'Reconnect failed');
      setDisconnecting(false);
    }
  };

  // ── Delete account handler ─────────────────────────────────

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;
    setDeleting(true);
    setDeleteError('');
    try {
      const token = await getAuthToken();
      const res = await fetch(`${API_URL}/api/account/delete`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Delete failed: ${res.status}`);
      }
      // Sign out and redirect to landing
      await signOut();
      navigate('/');
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account. Please contact support.');
      setDeleting(false);
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Settings</h1>
      <p className={styles.subtitle}>Manage your account, security, and connections.</p>

      <div className={styles.tabBar}>
        {TAB_LABELS.map((t) => (
          <button
            key={t.key}
            className={activeTab === t.key ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Account Info ── */}
      {activeTab === 'account' && (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Account</h2>
            <div className={styles.card}>
              <div className={styles.row}>
                <span className={styles.label}>Username</span>
                <span className={styles.value}>{user?.username || '—'}</span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>User ID</span>
                <span className={`${styles.value} ${styles.mono}`}>{user?.userId?.slice(0, 8)}...</span>
              </div>
              <div className={styles.row}>
                <span className={styles.label}>Role</span>
                <span className={`${styles.badge} ${user?.role === 'admin' ? styles.badgeOn : styles.badgeOff}`}>
                  {user?.role || '—'}
                </span>
              </div>
            </div>
          </section>

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Help</h2>
            <div className={styles.card}>
              <div className={styles.mfaActions}>
                <p className={styles.mfaDesc}>Re-watch the guided tour to learn about key features and navigation.</p>
                <button
                  className={styles.btnSecondary}
                  onClick={() => {
                    localStorage.removeItem('tour_completed');
                    localStorage.removeItem('shepherd-tour');
                    navigate('/home');
                    window.location.reload();
                  }}
                >
                  Restart Guided Tour
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── Alpaca Connection ── */}
      {activeTab === 'broker' && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Brokerage Connection</h2>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Paper Account</span>
              <span className={`${styles.badge} ${paperConnected ? styles.badgeOn : styles.badgeOff}`}>
                {paperConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Live Account</span>
              <span className={`${styles.badge} ${liveConnected ? styles.badgeOn : styles.badgeOff}`}>
                {liveConnected ? 'Connected' : 'Not Connected'}
              </span>
            </div>

            {alpacaMessage && <p className={styles.success}>{alpacaMessage}</p>}

            <div className={styles.connectionActions}>
              {!isConnected ? (
                <button className={styles.btnPrimary} onClick={() => navigate('/onboarding')}>
                  Connect Alpaca Account
                </button>
              ) : (
                <div className={styles.disconnectBtns}>
                  {paperConnected && (
                    <>
                      <button
                        className={styles.btnPrimary}
                        onClick={() => handleReconnect('paper')}
                        disabled={disconnecting}
                        title="Wipe stored credentials and start a fresh OAuth flow — use this if you're seeing 401 errors on paper"
                      >
                        Reconnect Paper
                      </button>
                      <button
                        className={styles.btnSecondary}
                        onClick={() => handleDisconnect('paper')}
                        disabled={disconnecting}
                      >
                        Disconnect Paper
                      </button>
                    </>
                  )}
                  {liveConnected && (
                    <>
                      <button
                        className={styles.btnPrimary}
                        onClick={() => handleReconnect('live')}
                        disabled={disconnecting}
                        title="Wipe stored credentials and start a fresh OAuth flow — use this if you're seeing 401 errors on live"
                      >
                        Reconnect Live
                      </button>
                      <button
                        className={styles.btnSecondary}
                        onClick={() => handleDisconnect('live')}
                        disabled={disconnecting}
                      >
                        Disconnect Live
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Notification Preferences ── */}
      {activeTab === 'notifications' && (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Notifications &amp; Alerts</h2>
            <div className={styles.card}>
              {notifLoading ? (
                <p className={styles.muted}>Loading notification settings...</p>
              ) : (
                <>
                  {notifError && <p className={styles.error}>{notifError}</p>}
                  {notifSuccess && <p className={styles.success}>{notifSuccess}</p>}

                  <p className={styles.mfaDesc}>
                    Get alerts when your positions hit profit targets or stop-loss levels. Receive notifications in-app
                    and via SMS.
                  </p>

                  {/* ── Channel toggles ── */}
                  <div className={styles.notifToggles}>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>In-App Notifications</span>
                      <input
                        type="checkbox"
                        checked={notifInAppEnabled}
                        onChange={(e) => setNotifInAppEnabled(e.target.checked)}
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>SMS Notifications</span>
                      <input
                        type="checkbox"
                        checked={notifSmsEnabled}
                        onChange={(e) => setNotifSmsEnabled(e.target.checked)}
                        className={styles.toggle}
                      />
                    </label>
                  </div>

                  {/* ── SMS opt-in + phone number ── */}
                  {notifSmsEnabled && (
                    <div className={styles.phoneSection}>
                      <div
                        style={{
                          background: 'rgba(59, 130, 246, 0.06)',
                          border: '1px solid rgba(59, 130, 246, 0.15)',
                          borderRadius: 10,
                          padding: '14px 16px',
                          marginBottom: 16,
                          fontSize: '0.82rem',
                          lineHeight: 1.6,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        <strong style={{ color: 'var(--text-primary)' }}>SMS Alert Consent from SignalDeck</strong>
                        <br />
                        By entering your phone number and enabling SMS notifications, you agree to receive automated
                        trading alert messages from SignalDeck. These include position alerts, stop-loss triggers,
                        profit targets, and strategist notifications.
                        <br />
                        <br />
                        Message frequency varies based on market activity and your alert settings. Message and data
                        rates may apply. Reply <strong>STOP</strong> to opt-out of all SMS messages at any time. Reply{' '}
                        <strong>HELP</strong> for help.
                        <br />
                        <br />
                        <a href="/terms" style={{ color: 'var(--accent)' }}>
                          Terms &amp; Conditions
                        </a>{' '}
                        |{' '}
                        <a href="/privacy" style={{ color: 'var(--accent)' }}>
                          Privacy Policy
                        </a>
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Phone Number (E.164 format)
                          {notifPhoneVerified && (
                            <span className={`${styles.badge} ${styles.badgeOn}`} style={{ marginLeft: 8 }}>
                              Verified
                            </span>
                          )}
                        </label>
                        <div className={styles.phoneRow}>
                          <input
                            type="tel"
                            value={notifPhone}
                            onChange={(e) => setNotifPhone(e.target.value)}
                            placeholder="+12125551234"
                            className={styles.formInput}
                          />
                          {notifPhone && !notifPhoneVerified && (
                            <button
                              className={styles.btnSecondary}
                              onClick={handleSendVerification}
                              disabled={notifVerifySending}
                              type="button"
                            >
                              {notifVerifySending ? 'Sending...' : 'Verify'}
                            </button>
                          )}
                        </div>
                      </div>

                      {showVerifyInput && !notifPhoneVerified && (
                        <div className={styles.verifySection}>
                          <label className={styles.formLabel}>Enter 6-digit verification code:</label>
                          <div className={styles.phoneRow}>
                            <input
                              type="text"
                              value={notifVerifyCode}
                              onChange={(e) => setNotifVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="000000"
                              className={styles.formInput}
                              maxLength={6}
                              inputMode="numeric"
                              style={{ maxWidth: 160, letterSpacing: 4, fontFamily: 'monospace', textAlign: 'center' }}
                            />
                            <button
                              className={styles.btnPrimary}
                              onClick={handleConfirmVerification}
                              disabled={notifVerifyConfirming || notifVerifyCode.length !== 6}
                              type="button"
                            >
                              {notifVerifyConfirming ? 'Confirming...' : 'Confirm'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Email notifications ── */}
                  <label className={styles.toggleRow} style={{ marginTop: 12 }}>
                    <span className={styles.toggleLabel}>Email Notifications</span>
                    <input
                      type="checkbox"
                      checked={notifEmailEnabled}
                      onChange={(e) => setNotifEmailEnabled(e.target.checked)}
                      className={styles.toggle}
                    />
                  </label>

                  {notifEmailEnabled && (
                    <div className={styles.phoneSection}>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Email Address
                          <span style={{ fontSize: 11, color: '#64748b', marginLeft: 8 }}>
                            Separate multiple with commas
                          </span>
                        </label>
                        <input
                          type="email"
                          value={notifEmail}
                          onChange={(e) => setNotifEmail(e.target.value)}
                          placeholder="you@example.com"
                          className={styles.formInput}
                        />
                      </div>
                    </div>
                  )}

                  {/* ── Alert types ── */}
                  <div className={styles.notifSection}>
                    <h3 className={styles.notifSubhead}>Alert Types</h3>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Position threshold (TP/SL)</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.positionThreshold}
                        onChange={(e) =>
                          setNotifAlertTypes((prev) => ({ ...prev, positionThreshold: e.target.checked }))
                        }
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Bot signals</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.botSignal}
                        onChange={(e) => setNotifAlertTypes((prev) => ({ ...prev, botSignal: e.target.checked }))}
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Stop-loss triggered</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.stopTriggered}
                        onChange={(e) => setNotifAlertTypes((prev) => ({ ...prev, stopTriggered: e.target.checked }))}
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Unusual volume alerts</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.unusualVolume}
                        onChange={(e) => setNotifAlertTypes((prev) => ({ ...prev, unusualVolume: e.target.checked }))}
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Claude Strategist trades</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.strategist}
                        onChange={(e) => setNotifAlertTypes((prev) => ({ ...prev, strategist: e.target.checked }))}
                        className={styles.toggle}
                      />
                    </label>
                    <label className={styles.toggleRow}>
                      <span className={styles.toggleLabel}>Morning Brief (7 AM daily)</span>
                      <input
                        type="checkbox"
                        checked={notifAlertTypes.morningBrief}
                        onChange={(e) => setNotifAlertTypes((prev) => ({ ...prev, morningBrief: e.target.checked }))}
                        className={styles.toggle}
                      />
                    </label>
                  </div>

                  {/* ── Thresholds ── */}
                  <div className={styles.notifSection}>
                    <h3 className={styles.notifSubhead}>Profit &amp; Loss Thresholds</h3>
                    <div className={styles.thresholdGrid}>
                      <div className={styles.thresholdField}>
                        <label className={styles.formLabel}>TP1 %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={notifThresholds.tp1Pct}
                          onChange={(e) => setNotifThresholds((prev) => ({ ...prev, tp1Pct: e.target.value }))}
                          className={styles.formInput}
                        />
                      </div>
                      <div className={styles.thresholdField}>
                        <label className={styles.formLabel}>TP2 %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={notifThresholds.tp2Pct}
                          onChange={(e) => setNotifThresholds((prev) => ({ ...prev, tp2Pct: e.target.value }))}
                          className={styles.formInput}
                        />
                      </div>
                      <div className={styles.thresholdField}>
                        <label className={styles.formLabel}>TP3 %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={notifThresholds.tp3Pct}
                          onChange={(e) => setNotifThresholds((prev) => ({ ...prev, tp3Pct: e.target.value }))}
                          className={styles.formInput}
                        />
                      </div>
                      <div className={styles.thresholdField}>
                        <label className={styles.formLabel}>Stop Loss %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={notifThresholds.stopLossPct}
                          onChange={(e) => setNotifThresholds((prev) => ({ ...prev, stopLossPct: e.target.value }))}
                          className={styles.formInput}
                        />
                      </div>
                      <div className={styles.thresholdField}>
                        <label className={styles.formLabel}>Trailing Stop %</label>
                        <input
                          type="number"
                          step="0.1"
                          value={notifThresholds.trailingStopPct}
                          onChange={(e) => setNotifThresholds((prev) => ({ ...prev, trailingStopPct: e.target.value }))}
                          className={styles.formInput}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={styles.setupBtns} style={{ marginTop: 16 }}>
                    <button className={styles.btnPrimary} onClick={handleSaveNotifications} disabled={notifSaving}>
                      {notifSaving ? 'Saving...' : 'Save Notification Settings'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>

          <ConnectedDevices />
        </>
      )}

      {/* ── Change Password ── */}
      {activeTab === 'security' && (
        <>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Password</h2>
            <div className={styles.card}>
              {passwordError && <p className={styles.error}>{passwordError}</p>}
              {passwordSuccess && <p className={styles.success}>{passwordSuccess}</p>}

              {!showPasswordForm ? (
                <div className={styles.mfaActions}>
                  <p className={styles.mfaDesc}>
                    Change your account password. You'll need to enter your current password to verify.
                  </p>
                  <button className={styles.btnPrimary} onClick={() => setShowPasswordForm(true)}>
                    Change Password
                  </button>
                </div>
              ) : (
                <form onSubmit={handleChangePassword} className={styles.passwordForm}>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Current Password</label>
                    <input
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className={styles.formInput}
                      required
                      autoComplete="current-password"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>New Password</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className={styles.formInput}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={styles.formInput}
                      required
                      minLength={8}
                      autoComplete="new-password"
                    />
                  </div>
                  <div className={styles.setupBtns}>
                    <button type="submit" className={styles.btnPrimary} disabled={changingPassword}>
                      {changingPassword ? 'Changing...' : 'Update Password'}
                    </button>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => {
                        setShowPasswordForm(false);
                        setCurrentPassword('');
                        setNewPassword('');
                        setConfirmPassword('');
                        setPasswordError('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>
          </section>

          {/* ── Two-Factor Authentication ── */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Two-Factor Authentication (2FA)</h2>
            <div className={styles.card}>
              {mfaLoading ? (
                <p className={styles.muted}>Checking MFA status...</p>
              ) : (
                <>
                  <div className={styles.row}>
                    <span className={styles.label}>Status</span>
                    <span className={`${styles.badge} ${mfaEnabled ? styles.badgeOn : styles.badgeOff}`}>
                      {mfaEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>

                  {mfaError && <p className={styles.error}>{mfaError}</p>}
                  {mfaSuccess && <p className={styles.success}>{mfaSuccess}</p>}

                  {!mfaEnabled && !setupMode && (
                    <div className={styles.mfaActions}>
                      <p className={styles.mfaDesc}>
                        Add an extra layer of security by requiring a code from your authenticator app (Google
                        Authenticator, Authy, 1Password, etc.) when signing in.
                      </p>
                      <button className={styles.btnPrimary} onClick={handleStartSetup}>
                        Set Up 2FA
                      </button>
                    </div>
                  )}

                  {setupMode && (
                    <div className={styles.setupFlow}>
                      <h3 className={styles.setupTitle}>Scan this QR code with your authenticator app</h3>
                      <div className={styles.qrContainer}>
                        <QRCodeSVG
                          value={qrUri}
                          size={200}
                          bgColor="transparent"
                          fgColor="var(--text-primary)"
                          level="M"
                        />
                      </div>
                      <details className={styles.manualEntry}>
                        <summary>Can't scan? Enter manually</summary>
                        <code className={styles.secretKey}>{secretKey}</code>
                      </details>
                      <form onSubmit={handleVerifyAndEnable} className={styles.verifyForm}>
                        <label className={styles.verifyLabel}>Enter the 6-digit code to verify:</label>
                        <input
                          type="text"
                          value={verifyCode}
                          onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className={styles.verifyInput}
                          autoComplete="one-time-code"
                          inputMode="numeric"
                          maxLength={6}
                          autoFocus
                          required
                        />
                        <div className={styles.setupBtns}>
                          <button
                            type="submit"
                            className={styles.btnPrimary}
                            disabled={submitting || verifyCode.length !== 6}
                          >
                            {submitting ? 'Verifying...' : 'Verify & Enable'}
                          </button>
                          <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => {
                              setSetupMode(false);
                              setQrUri('');
                              setSecretKey('');
                              setVerifyCode('');
                              setMfaError('');
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {mfaEnabled && !setupMode && (
                    <div className={styles.mfaActions}>
                      <p className={styles.mfaDesc}>
                        Your account is protected with two-factor authentication. You'll be prompted for a code from
                        your authenticator app when signing in.
                      </p>
                      <button className={styles.btnDanger} onClick={handleDisable} disabled={submitting}>
                        {submitting ? 'Disabling...' : 'Disable 2FA'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        </>
      )}

      {/* ── Danger Zone — Delete Account ── */}
      {activeTab === 'danger' && (
        <section className={styles.section}>
          <h2 className={`${styles.sectionTitle} ${styles.dangerTitle}`}>Danger Zone</h2>
          <div className={`${styles.card} ${styles.dangerCard}`}>
            <p className={styles.mfaDesc}>
              Permanently delete your account and all associated data. This action cannot be undone. Your Cognito
              account, DynamoDB records, and S3 data will be purged.
            </p>

            {deleteError && <p className={styles.error}>{deleteError}</p>}

            {!showDeleteConfirm ? (
              <button className={styles.btnDanger} onClick={() => setShowDeleteConfirm(true)}>
                Delete My Account
              </button>
            ) : (
              <div className={styles.deleteConfirm}>
                <p className={styles.deleteWarning}>
                  Type <strong>DELETE</strong> to confirm account deletion:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className={styles.formInput}
                  placeholder="Type DELETE"
                  autoFocus
                />
                <div className={styles.setupBtns}>
                  <button
                    className={styles.btnDanger}
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmText !== 'DELETE' || deleting}
                  >
                    {deleting ? 'Deleting...' : 'Permanently Delete Account'}
                  </button>
                  <button
                    className={styles.btnSecondary}
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setDeleteConfirmText('');
                      setDeleteError('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default Settings;
